import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api/client';
import { Plus, Pencil, Trash2, Save, X, Loader2 } from 'lucide-react';
import ImageUpload from './ImageUpload';
import RichTextEditor from './RichTextEditor';
import { buildImageUrl } from '../../api/client';

/**
 * Generic CRUD table for an entity.
 * Props:
 *   entity: string (backend name, e.g. "services")
 *   title: string
 *   fields: [{ key, label, type: 'text'|'textarea'|'number'|'image'|'select'|'list'|'imageList', options?, rows? }]
 *   listColumns: [{ key, label, render?(v,row) }]
 *   defaults: object of initial values for a new item
 */
export default function CrudTable({ entity, title, fields, listColumns, defaults = {} }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed; {} = new; {id...} = edit
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.list(entity);
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [entity]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => setEditing({ ...defaults });
  const openEdit = (row) => setEditing({ ...row });
  const close = () => { setEditing(null); setErr(''); };

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      const payload = { ...editing };
      // Numeric coercion
      fields.forEach((f) => { if (f.type === 'number') payload[f.key] = Number(payload[f.key] || 0); });
      
      // Form validation
      for (const f of fields) {
        const value = payload[f.key];
        if (f.type === 'text' && !value && f.key !== 'image') {
          setErr(`${f.label} mütləqdir`);
          return;
        }
        if (f.type === 'number' && (value === undefined || value === null || value === '')) {
          setErr(`${f.label} mütləqdir`);
          return;
        }
      }
      
      if (editing.id) {
        await adminApi.update(entity, editing.id, payload);
      } else {
        await adminApi.create(entity, payload);
      }
      await load();
      close();
      alert(editing.id ? 'Yadda saxlandı' : 'Əlavə edildi');
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Yadda saxlanılmadı');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Silməyə əminsiniz?')) return;
    await adminApi.remove(entity, id);
    await load();
  };

  const setField = (k, v) => setEditing((prev) => ({ ...prev, [k]: v }));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{title}</h2>
        <button onClick={openNew} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#17b4b4', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          <Plus size={16} /> Yeni əlavə et
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}><Loader2 className="spin" size={22} /></div>
        ) : items.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Heç bir qeyd yoxdur</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f8fafc' }}>
              <tr>
                {listColumns.map((c) => (
                  <th key={c.key} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 }}>{c.label}</th>
                ))}
                <th style={{ padding: '12px 16px', width: 120 }}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  {listColumns.map((c) => (
                    <td key={c.key} style={{ padding: '12px 16px', fontSize: 13.5, color: '#1f2937', verticalAlign: 'middle', maxWidth: 400 }}>
                      {c.render ? c.render(row[c.key], row) : (typeof row[c.key] === 'string' && row[c.key].length > 80 ? row[c.key].slice(0, 80) + '…' : row[c.key])}
                    </td>
                  ))}
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    <button onClick={() => openEdit(row)} style={{ background: 'transparent', border: '1px solid #e5e7eb', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#17b4b4', marginRight: 6 }}><Pencil size={14} /></button>
                    <button onClick={() => remove(row.id)} style={{ background: 'transparent', border: '1px solid #fecaca', borderRadius: 6, padding: 6, cursor: 'pointer', color: '#dc2626' }}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing !== null && (
        <div onClick={close} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{editing.id ? 'Redaktə et' : 'Yeni əlavə et'}</h3>
              <button onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={20} /></button>
            </div>
            <div style={{ padding: 22 }}>
              {fields.map((f) => (
                <FieldInput key={f.key} field={f} value={editing[f.key]} onChange={(v) => setField(f.key, v)} />
              ))}
              {err && <div style={{ color: '#dc2626', background: '#fef2f2', padding: 10, borderRadius: 6, marginTop: 8, fontSize: 13 }}>{err}</div>}
            </div>
            <div style={{ padding: '14px 22px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={close} style={{ padding: '10px 18px', border: '1px solid #d1d5db', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>Ləğv et</button>
              <button onClick={save} disabled={saving} style={{ padding: '10px 18px', background: '#17b4b4', color: '#fff', border: 'none', borderRadius: 8, cursor: saving ? 'wait' : 'pointer', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Yadda saxla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const common = { width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', outline: 'none' };
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#374151' }}>{field.label}</label>
      {field.type === 'textarea' && (
        <textarea value={value || ''} onChange={(e) => onChange(e.target.value)} rows={field.rows || 6} style={{ ...common, resize: 'vertical', fontFamily: 'inherit' }} />
      )}
      {field.type === 'richText' && (
        <RichTextEditor value={value || ''} onChange={onChange} />
      )}
      {field.type === 'number' && (
        <input type="number" value={value ?? 0} onChange={(e) => onChange(e.target.value)} style={common} />
      )}
      {field.type === 'select' && (
        <select value={value || ''} onChange={(e) => onChange(e.target.value)} style={common}>
          {(field.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {field.type === 'image' && (
        <ImageUpload value={value} onChange={onChange} label="" />
      )}
      {field.type === 'list' && (
        <ListEditor value={value || []} onChange={onChange} />
      )}
      {field.type === 'imageList' && (
        <ImageListEditor value={value || []} onChange={onChange} />
      )}
      {(!field.type || field.type === 'text') && (
        <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} style={common} />
      )}
    </div>
  );
}

function ListEditor({ value, onChange }) {
  const set = (i, v) => onChange(value.map((x, idx) => idx === i ? v : x));
  const add = () => onChange([...value, '']);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));
  return (
    <div>
      {value.map((v, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input value={v} onChange={(e) => set(i, e.target.value)} style={{ flex: 1, padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
          <button type="button" onClick={() => del(i)} style={{ background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: 6, padding: '0 10px', cursor: 'pointer' }}><Trash2 size={13} /></button>
        </div>
      ))}
      <button type="button" onClick={add} style={{ background: 'transparent', border: '1px dashed #17b4b4', color: '#17b4b4', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>+ Element əlavə et</button>
    </div>
  );
}

function ImageListEditor({ value, onChange }) {
  const add = () => onChange([...value, '']);
  const del = (i) => onChange(value.filter((_, idx) => idx !== i));
  const set = (i, v) => onChange(value.map((x, idx) => idx === i ? v : x));

  return (
    <div>
      {value.map((v, i) => (
        <div key={i} style={{ marginBottom: 12, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <ImageUpload value={v} onChange={(val) => set(i, val)} label="" />
            <button type="button" onClick={() => del(i)} style={{ background: '#fee2e2', border: 'none', color: '#dc2626', borderRadius: 6, padding: '8px 12px', cursor: 'pointer', height: 'fit-content' }}><Trash2 size={14} /></button>
          </div>
          {v && (
            <img 
              src={buildImageUrl(v)} 
              alt={`Slide ${i + 1}`} 
              style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 4, marginTop: 8 }}
            />
          )}
        </div>
      ))}
      <button type="button" onClick={add} style={{ background: 'transparent', border: '1px dashed #17b4b4', color: '#17b4b4', padding: '8px 16px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, width: '100%' }}>+ Slayd əlavə et</button>
    </div>
  );
}

export const ImageCell = (v) => (
  v ? <img src={buildImageUrl(v)} alt="" style={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ color: '#9ca3af' }}>—</span>
);
